import React, { createContext, useContext, useState, useEffect } from 'react';
import { initialCategories, initialManuals } from '../utils/constants';
import { Folder } from 'lucide-react';

const ManualsContext = createContext();
export const useManuals = () => useContext(ManualsContext);

export const ManualsProvider = ({ children }) => {
  const [categories, setCategories] = useState(initialCategories);
  const [manuals, setManuals] = useState(initialManuals);
  const [activeCategory, setActiveCategory] = useState('ap12');
  const [activeManual, setActiveManual] = useState('ap12-1');
  const [searchQuery, setSearchQuery] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newManualTitle, setNewManualTitle] = useState('');
  const [newManualCategory, setNewManualCategory] = useState('ap12');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [manualToDelete, setManualToDelete] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showDeleteManualModal, setShowDeleteManualModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isManagingUsers, setIsManagingUsers] = useState(false);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [showMediaModal, setShowMediaModal] = useState(false);

  // Фильтрация руководств по поисковому запросу
  const filteredManuals = Object.entries(manuals).reduce((acc, [categoryId, manualsList]) => {
    if (!searchQuery) {
      acc[categoryId] = manualsList;
      return acc;
    }
    const filtered = manualsList.filter(manual =>
      manual.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      manual.content.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) acc[categoryId] = filtered;
    return acc;
  }, {});

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
    
    // Для категории "reports" не устанавливаем activeManual автоматически
    // и не закрываем панель на мобильных устройствах
    if (categoryId === 'reports') {
      setActiveManual(null);
      return; // Не закрываем панель
    }
    
    const categoryManuals = filteredManuals[categoryId] || manuals[categoryId] || [];
    if (categoryManuals.length > 0) {
      setActiveManual(categoryManuals[0].id);
    } else {
      setActiveManual(null);
    }
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const handleManualClick = (manualId) => {
    setActiveManual(manualId);
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const addManual = (categoryId, title) => {
    const newId = `manual-${Date.now()}`;
    const now = new Date().toISOString();
    const newManual = {
      id: newId,
      title,
      content: '<p>Начните вводить содержимое здесь...</p>',
      createdAt: now,
      updatedAt: now,
    };
    setManuals(prev => {
      const updated = { ...prev };
      const categoryManuals = [...(updated[categoryId] || [])];
      categoryManuals.push(newManual);
      updated[categoryId] = categoryManuals;
      return updated;
    });
    setActiveCategory(categoryId);
    setActiveManual(newId);
    setIsAddingManual(false);
    setNewManualTitle('');
    return newManual;
  };

  const saveContent = (categoryId, manualId, newContent) => {
    setManuals(prev => {
      const updated = { ...prev };
      const categoryManuals = [...(updated[categoryId] || [])];
      const index = categoryManuals.findIndex(m => m.id === manualId);
      if (index !== -1) {
        categoryManuals[index] = {
          ...categoryManuals[index],
          content: newContent,
          updatedAt: new Date().toISOString(),
        };
        updated[categoryId] = categoryManuals;
      }
      return updated;
    });
    setEditorContent(newContent);
  };

  const addCategory = (name) => {
    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      alert('Раздел с таким названием уже существует');
      return null;
    }
    const newId = `cat-${Date.now()}`;
    const newCategory = {
      id: newId,
      name,
      icon: <Folder className="w-5 h-5" />,
    };
    setCategories(prev => [...prev, newCategory]);
    setManuals(prev => ({ ...prev, [newId]: [] }));
    setActiveCategory(newId);
    setIsAddingCategory(false);
    setNewCategoryName('');
    return newCategory;
  };

  const deleteManual = (categoryId, manualId) => {
    setManuals(prev => {
      const updated = { ...prev };
      const categoryManuals = updated[categoryId]?.filter(m => m.id !== manualId) || [];
      updated[categoryId] = categoryManuals;
      return updated;
    });
    if (activeManual === manualId) {
      const remaining = manuals[categoryId]?.filter(m => m.id !== manualId) || [];
      if (remaining.length > 0) setActiveManual(remaining[0].id);
      else setActiveManual(null);
    }
  };

  const deleteCategory = (categoryId) => {
    setCategories(prev => prev.filter(c => c.id !== categoryId));
    setManuals(prev => {
      const updated = { ...prev };
      delete updated[categoryId];
      return updated;
    });
    if (activeCategory === categoryId) {
      const remainingCategories = categories.filter(c => c.id !== categoryId);
      if (remainingCategories.length > 0) setActiveCategory(remainingCategories[0].id);
      else setActiveCategory(null);
    }
  };

  // При изменении activeManual обновляем editorContent
  useEffect(() => {
    if (activeManual) {
      const categoryManuals = manuals[activeCategory] || [];
      const manual = categoryManuals.find(m => m.id === activeManual);
      if (manual) {
        setEditorContent(manual.content || '');
      } else {
        setEditorContent('');
      }
      setIsEditMode(false);
      setPreviewMode(true);
    }
  }, [activeManual, activeCategory, manuals]);

  return (
    <ManualsContext.Provider value={{
      categories,
      manuals,
      activeCategory, setActiveCategory,
      activeManual, setActiveManual,
      searchQuery, setSearchQuery,
      editorContent, setEditorContent,
      isEditMode, setIsEditMode,
      previewMode, setPreviewMode,
      isSidebarOpen, setIsSidebarOpen,
      isAddingManual, setIsAddingManual,
      isAddingCategory, setIsAddingCategory,
      newManualTitle, setNewManualTitle,
      newManualCategory, setNewManualCategory,
      newCategoryName, setNewCategoryName,
      manualToDelete, setManualToDelete,
      categoryToDelete, setCategoryToDelete,
      showDeleteManualModal, setShowDeleteManualModal,
      showDeleteCategoryModal, setShowDeleteCategoryModal,
      showAdminPanel, setShowAdminPanel,
      isManagingUsers, setIsManagingUsers,
      passwordResetRequests, setPasswordResetRequests,
      showMediaModal, setShowMediaModal,
      filteredManuals,
      handleCategoryClick,
      handleManualClick,
      addManual,
      saveContent,
      addCategory,
      deleteManual,
      deleteCategory,
    }}>
      {children}
    </ManualsContext.Provider>
  );
};